/**
 * Money **input** boundary (W1-ADMIN, D358): turn what an operator types in major units — in their locale's decimal
 * convention — into exact integer minor units of an ISO 4217 currency, and back into an editable string.
 *
 * Why this exists: operator consoles used to ask for "amount in minor units" (the operator typed `1250` meaning
 * 12.50), which is error-prone by a factor of 10^exponent and meaningless for a zero-decimal currency. The domain
 * stays integer minor units (ADR-0007); only the human edge changes.
 *
 * Rules (all pure, no float, no country default):
 * - The currency's minor-unit exponent comes from `Intl` (`currencyMinorUnits`): 2 for most, 0 for JPY-like, 3 for
 *   BHD-like. More fraction digits than the exponent allows is refused (`TOO_MANY_DECIMALS`), never rounded.
 * - The locale's decimal and grouping separators come from `Intl`. Both `.` and `,` are accepted as a decimal point
 *   so an operator typing the "other" convention is not silently misread: when both appear, the last one is the
 *   decimal point; when one appears once it is a decimal point unless it is the locale's grouping separator
 *   followed by exactly three digits (`1.000` in a dot-grouping locale is one thousand). Grouping must be
 *   well-formed (`1.234.567`), otherwise the input is refused as `INVALID` rather than guessed.
 * - Negative amounts are refused (`NEGATIVE`); amounts above the signed 64-bit minor-unit ceiling, or above a
 *   caller-supplied maximum, are refused (`OVERFLOW`).
 * The caller shows a formatted preview of the parsed value, so the interpretation is always visible before submit.
 */
import { MAX_MINOR, isIso4217Currency } from '@vello/core';
import { currencyMinorUnits } from './format.js';
// Whitespace a locale may use for grouping (regular, no-break, narrow no-break, thin) — removed before parsing.
const SPACES = /[\s\u00a0\u202f\u2009]/g;
/** The locale's decimal and grouping separators, as `Intl` renders them. */
export function localeSeparators(locale) {
    const parts = new Intl.NumberFormat(locale, { useGrouping: true }).formatToParts(1234567.5);
    return {
        decimal: parts.find((p) => p.type === 'decimal')?.value ?? '.',
        group: parts.find((p) => p.type === 'group')?.value ?? '',
    };
}
/** The exponent for a currency, or undefined when `Intl` does not know the code. */
function exponentOf(currency) {
    // An assigned ISO 4217 code only: `Intl` happily formats an unassigned code like `AAA` with two decimals.
    if (!/^[A-Z]{3}$/.test(currency) || !isIso4217Currency(currency))
        return undefined;
    try {
        return currencyMinorUnits(currency);
    }
    catch {
        return undefined;
    }
}
/** True when `integer` is a well-formed grouped integer using `sep` (first group 1–3 digits, then groups of 3). */
function wellGrouped(integer, sep) {
    const groups = integer.split(sep);
    if (groups.length < 2)
        return /^\d+$/.test(integer);
    const [head, ...tail] = groups;
    return head !== undefined && /^\d{1,3}$/.test(head) && tail.every((g) => /^\d{3}$/.test(g));
}
/**
 * Parse an operator-typed major-unit amount into integer minor units of `currency`, reading separators for `locale`.
 * Never throws for user input; returns a stable issue code instead.
 */
export function parseMoneyInput(raw, currency, locale, opts = {}) {
    const exponent = exponentOf(currency);
    if (exponent === undefined)
        return { ok: false, issue: 'INVALID_CURRENCY' };
    const text = raw.replace(SPACES, '');
    if (text === '')
        return { ok: false, issue: 'EMPTY' };
    if (text.startsWith('-') || text.startsWith('\u2212')) {
        return /^[-\u2212][\d.,]*\d[\d.,]*$/.test(text) ? { ok: false, issue: 'NEGATIVE' } : { ok: false, issue: 'INVALID' };
    }
    if (!/^[\d.,]+$/.test(text) || !/\d/.test(text))
        return { ok: false, issue: 'INVALID' };
    const { group } = localeSeparators(locale);
    const dots = text.split('.').length - 1;
    const commas = text.split(',').length - 1;
    let integer = text;
    let fraction = '';
    if (dots > 0 && commas > 0) {
        // Both present: the last one is the decimal point and must appear exactly once; the other is grouping.
        const decimalChar = text.lastIndexOf('.') > text.lastIndexOf(',') ? '.' : ',';
        const groupChar = decimalChar === '.' ? ',' : '.';
        if ((decimalChar === '.' ? dots : commas) !== 1)
            return { ok: false, issue: 'INVALID' };
        const at = text.lastIndexOf(decimalChar);
        integer = text.slice(0, at);
        fraction = text.slice(at + 1);
        if (integer.includes(decimalChar) || !wellGrouped(integer, groupChar))
            return { ok: false, issue: 'INVALID' };
        integer = integer.split(groupChar).join('');
    }
    else if (dots + commas === 1) {
        const sepChar = dots === 1 ? '.' : ',';
        const at = text.indexOf(sepChar);
        const before = text.slice(0, at);
        const after = text.slice(at + 1);
        const isLocaleGrouping = sepChar === group && /^\d{3}$/.test(after) && /^\d{1,3}$/.test(before);
        if (isLocaleGrouping) {
            integer = before + after;
        }
        else {
            integer = before;
            fraction = after;
            if (fraction === '')
                return { ok: false, issue: 'INVALID' }; // a dangling separator ("12,") is not a number
        }
    }
    else if (dots + commas > 1) {
        // One separator repeated: only valid as grouping.
        const sepChar = dots > 0 ? '.' : ',';
        if (!wellGrouped(text, sepChar))
            return { ok: false, issue: 'INVALID' };
        integer = text.split(sepChar).join('');
    }
    if (!/^\d*$/.test(integer) || !/^\d*$/.test(fraction))
        return { ok: false, issue: 'INVALID' };
    if (fraction.length > exponent)
        return { ok: false, issue: 'TOO_MANY_DECIMALS' };
    const whole = integer === '' ? 0n : BigInt(integer);
    const minorPart = fraction === '' ? 0n : BigInt(fraction.padEnd(exponent, '0'));
    const amountMinor = whole * 10n ** BigInt(exponent) + minorPart;
    const ceiling = opts.maxMinor !== undefined && opts.maxMinor < MAX_MINOR ? opts.maxMinor : MAX_MINOR;
    if (amountMinor > ceiling)
        return { ok: false, issue: 'OVERFLOW' };
    return { ok: true, amountMinor, exponent };
}
/**
 * Render integer minor units as the editable text a money input shows: the locale's decimal separator, exactly the
 * currency's exponent of fraction digits, no grouping (so re-parsing it is unambiguous). Returns `''` for an unknown
 * currency or a negative amount (inputs are non-negative).
 */
export function formatMoneyInput(amountMinor, currency, locale) {
    const exponent = exponentOf(currency);
    if (exponent === undefined || amountMinor < 0n)
        return '';
    const digits = amountMinor.toString();
    if (exponent === 0)
        return digits;
    const padded = digits.padStart(exponent + 1, '0');
    const cut = padded.length - exponent;
    const { decimal } = localeSeparators(locale);
    return `${padded.slice(0, cut)}${decimal}${padded.slice(cut)}`;
}
//# sourceMappingURL=money-input.js.map